import React, { ReactNode, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import './DashboardLayout.css'

interface DashboardLayoutProps {
    children: ReactNode
    sidebar: ReactNode
    header?: ReactNode
}

// When the app runs inside the parent ILPQC portal it's loaded in an <iframe>, and that
// portal already renders its own "Hello, <user>  Log off" header above the frame. Showing
// our own top bar there would duplicate it and steal height from the dashboard, so we only
// render the top bar when standalone (top-level window).
const isEmbedded = typeof window !== 'undefined' && window.self !== window.top

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, sidebar, header }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const { username, logout } = useAuth()

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen)
    }

    const closeSidebar = () => {
        setIsSidebarOpen(false)
    }

    return (
        <div className="dashboard-shell">
            {/* Top user bar: "Hello, <username>  Log off" -- mirrors the legacy ILPQC header
                (right-aligned nav item with a purple bottom border, "Log off" as a link).
                Hidden when embedded in the parent portal iframe, which renders its own. */}
            {!isEmbedded && (
                <div className="app-topbar">
                    <span className="app-topbar-nav">
                        {username && <span className="app-topbar-user">Hello, {username}</span>}
                        <button type="button" className="app-topbar-logoff" onClick={logout}>
                            Log off
                        </button>
                    </span>
                </div>
            )}

            <div className="dashboard-container">
                {/* Mobile Overlay */}
                {isSidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

                <div className={`sidebar-wrapper ${isSidebarOpen ? 'open' : ''}`}>
                    <div className="mobile-close-btn" onClick={closeSidebar}>&times;</div>
                    {sidebar}
                </div>

                <div className="content-area">
                    <div className="mobile-header-bar">
                        <button className="menu-toggle" onClick={toggleSidebar}>
                            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="3" y1="12" x2="21" y2="12"></line>
                                <line x1="3" y1="6" x2="21" y2="6"></line>
                                <line x1="3" y1="18" x2="21" y2="18"></line>
                            </svg>
                        </button>
                        {/* Re-using header in mobile bar if needed, or keeping it separate */}
                    </div>

                    {header && <header>{header}</header>}
                    <main className="main-content">{children}</main>
                </div>
            </div>
        </div>
    )
}

export default DashboardLayout
